import React, { useState, useEffect } from "react";
import "./CreateAccount.css";
import SpinnerOverlay from "../components/spinnerOverlay/SpinnerOverlay";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import api from "../utils/api";
import { toast } from "react-hot-toast";
import OTPModal from "../components/modals/OTPModal/OTPModal";
import { useSocialAuth } from "../hooks/useSocialAuth";
import { consumePostAuthRedirect } from "../utils/postAuthRedirect";

const CreateAccountPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const { user, updateUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]); // 6 digits
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  // const { firstName, lastName, email, phone, password } = formData;
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    academyName: "", // was trainingCenterName
    academyEmail: "",
    email: "",
    phone: "",
    password: "",
  });
  const {
    firstName,
    lastName,
    academyName,
    academyEmail,
    email,
    phone,
    password,
  } = formData;

  const [otpMethod, setOtpMethod] = useState("email");
  const { handleSocialAuth } = useSocialAuth();

  // Add ref for focusing next input
  const otpRefs = Array(6)
    .fill(0)
    .map(() => React.createRef());

  // Inside component
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const roleFromQuery = params.get("role");
  const selectedRole =
    location.state?.selectedRole || roleFromQuery || "student"; // default to student

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    clearServerError();
  };

  const clearServerError = () => setServerError("");

  // Password validation function
  const isPasswordValid = (pass) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumber = /\d/.test(pass);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    return (
      pass.length >= minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const validateForm = () => {
    const tempErrors = {};

    if (selectedRole === "institution") {
      if (!academyName.trim())
        tempErrors.academyName = "Academy name is required";
      if (!academyEmail.trim()) {
        tempErrors.academyEmail = "Academy email is required";
      } else if (!/\S+@\S+\.\S+/.test(academyEmail)) {
        tempErrors.academyEmail = "Academy email is invalid";
      }
    } else {
      if (!firstName.trim()) tempErrors.firstName = "First name is required";
      if (!lastName.trim()) tempErrors.lastName = "Last name is required";
      if (!email.trim()) {
        tempErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        tempErrors.email = "Email is invalid";
      }
    }

    if (!phone.trim()) {
      tempErrors.phone = "Phone number is required";
    } else if (!/^\+?[1-9]\d{1,14}$/.test(phone.replace(/\s/g, ""))) {
      tempErrors.phone = "Enter a valid phone number (e.g. +2348012345678)";
    }
    if (!password) {
      tempErrors.password = "Password is required";
    } else if (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/\d/.test(password) ||
      !/[!@#$%^&*]/.test(password)
    ) {
      tempErrors.password = "Password too weak";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const imageTexts = [
    {
      title: "Personalized Learning: ",
      subtitle: "Get the Best Tutor for You or Your Child. ",
    },
    { title: "Share Your Knowledge: ", subtitle: "Become a Tutor Today!" },
    {
      title: "Get Top-Notch Tutoring: ",
      subtitle: "For Kids, Adults, and Everyone In-Between.",
    },
    { title: "Share Your Knowledge: ", subtitle: "Become a Tutor Today!" },
  ];

  const images = [
    "../images/work-educational.jpg",
    "../images/tutoring.jpg",
    "../images/teaching-kid-write.jpg",
    "../images/study-group2.jpg",
  ];

  const criteriaMet = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*]/.test(password),
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setServerError("");

    const cleanedPhone = `+234${phone.replace(/\D/g, "")}`;

    const payload = {
      ...(selectedRole === "institution"
        ? {
            academyName: academyName.trim(),
            academyEmail: academyEmail.toLowerCase().trim(),
          }
        : {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.toLowerCase().trim(),
          }),
      phone: cleanedPhone,
      password,
      role: selectedRole,
      method: otpMethod,
    };

    try {
      const response = await api.post("/users/send-registration-otp", payload);
      console.log("response", response.data);
      // An unfinished registration resuming gets a real OTP too now (see
      // backend sendRegistrationOtp) — same modal either way, so
      // verifyOtp logs them into their EXISTING account instead of the
      // old behavior of silently navigating them to the form signed OUT
      // (no token), which is what caused drafts to "disappear": the
      // resume-prefill effect needs a real session to have anything to
      // prefill FROM.
      if (response.data.isResume) {
        toast.success(response.data.message || "Welcome back!");
      }
      setShowOtpModal(true);
    } catch (err) {
      const data = err.response?.data;

      // 🔴 ACCOUNT EXISTS — a genuinely completed profile, not a resume.
      if (err.response?.status === 409 || data?.code === "ACCOUNT_EXISTS") {
        const identifier = email ? payload.email : cleanedPhone;

        // ✅ show clear message
        toast.error("Account already exists. Redirecting to login...");

        // ✅ redirect with prefilled identifier
        setTimeout(() => {
          navigate("/login", {
            state: {
              identifier, // email or phone
              message: data?.message,
            },
          });
        }, 1200);

        return;
      }

      // 🔴 fallback error
      const message =
        data?.message || "Failed to send verification code. Please try again.";

      toast.error(message);
      setServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  // VERIFY OTP → GET USER + TOKEN DIRECTLY
  // ───────────────────────────────────────────────

  const verifyOtp = async (otpCode, method) => {
    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      setOtpError("Please enter a valid 6-digit code");
      return;
    }

    setOtpLoading(true);
    setOtpError("");
    setVerificationInProgress(true);

    // Use academyEmail for institution, regular email for others
    const verifyEmail =
      selectedRole === "institution"
        ? academyEmail.toLowerCase().trim()
        : email.toLowerCase().trim();

    try {
      const res = await api.post("/users/verify-registration-otp", {
        email: verifyEmail, // ← was always sending empty `email` for institution
        phone: `+234${phone.replace(/\D/g, "")}`,
        otp: otpCode,
        method,
      });

      const { token, ...userData } = res.data;
      login({ ...userData, role: selectedRole }, token);

      if (selectedRole === "student") {
        const redirectTo = consumePostAuthRedirect();
        navigate(redirectTo || "/student-home", { replace: true });
      } else if (selectedRole === "tutor") {
        navigate("/tutor/registration", { replace: true });
      } else if (selectedRole === "institution") {
        navigate("/academy/registration", { replace: true });
      }

      setShowOtpModal(false);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Invalid or expired code. Please try again.";
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
      setVerificationInProgress(false);
    }
  };
  // ────────────────────────────────────────────────
  // OTP INPUT HANDLERS (unchanged — looks good)
  // ────────────────────────────────────────────────
  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.current?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (pasted.length === 6 && /^\d{6}$/.test(pasted)) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.current?.focus();
    }
  };

  const handleResendOtp = async (method) => {
    setOtpError("");

    const resendEmail =
      selectedRole === "institution"
        ? academyEmail.toLowerCase().trim()
        : email.toLowerCase().trim();

    try {
      await api.post("/users/send-registration-otp", {
        ...(selectedRole === "institution"
          ? { academyName: academyName.trim(), academyEmail: resendEmail }
          : {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: resendEmail,
            }),
        phone: `+234${phone.replace(/\D/g, "")}`,
        password,
        role: selectedRole,
        method,
      });
    } catch (err) {
      setOtpError(err.response?.data?.message || "Failed to resend");
    }
  };

  // Password criteria check
  const getCriteriaColor = (criterionMet) =>
    criterionMet ? "green" : password ? "red" : "#f0f0f0";
  const minLengthMet = password.length >= 8;
  const upperCaseMet = /[A-Z]/.test(password);
  const lowerCaseMet = /[a-z]/.test(password);
  const numberMet = /\d/.test(password);
  const specialCharMet = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return (
    <div className="container">
      {isLoading && <SpinnerOverlay />}
      <div className="logo-container">
        <img
          src="../images/tutors-logo.png"
          alt="Website Logo"
          className="logo"
        />
      </div>
      <div className="form-container">
        {/* <h1 className="title">Create Account </h1> */}
        <h1 className="title">
          {selectedRole === "institution"
            ? "Register Training Center"
            : selectedRole === "tutor"
              ? "Join as a Tutor"
              : "Create Account"}
        </h1>
        <div className="form-section">
          {serverError && (
            <div className="server-error-banner">
              <p className="error">{serverError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {/* Conditionally render name fields based on role */}
            {selectedRole === "institution" ? (
              <>
                <div className="form-group">
                  <label>Training Center Name</label>
                  <input
                    type="text"
                    name="academyName"
                    placeholder="e.g. Bright Minds Academy"
                    value={academyName}
                    onChange={handleChange}
                    className="input-field"
                  />
                  {errors.academyName && (
                    <p className="error">{errors.academyName}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Training Center Email</label>
                  <input
                    type="email"
                    name="academyEmail"
                    placeholder="trainingcenter@example.com"
                    value={academyEmail}
                    onChange={handleChange}
                    className="input-field"
                  />
                  {errors.academyEmail && (
                    <p className="error">{errors.academyEmail}</p>
                  )}
                </div>
              </>
            ) : (
              <div className="form-group name-row">
                <div>
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={firstName}
                    onChange={handleChange}
                    className="input-field"
                  />
                  {errors.firstName && (
                    <p className="error">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label>Last Name</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    name="lastName"
                    value={lastName}
                    onChange={handleChange}
                    className="input-field"
                  />
                  {errors.lastName && (
                    <p className="error">{errors.lastName}</p>
                  )}
                </div>
              </div>
            )}
            {selectedRole !== "institution" && (
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="tutorslinks@gmail.com"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  className="input-field"
                />
                {errors.email && <p className="error">{errors.email}</p>}
              </div>
            )}

            <div className="form-group">
              <label>Phone Number</label>
              <div className="phone-input-wrapper">
                <span className="phone-prefix">+234</span>
                <input
                  type="tel"
                  name="phone"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "");
                    if (value.startsWith("0")) {
                      value = value.substring(1);
                    }
                    setFormData({ ...formData, phone: value });
                  }}
                  placeholder="801 234 5678"
                  className="phone-input"
                />
              </div>

              {errors.phone && <p className="error">{errors.phone}</p>}
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={handleChange}
                  placeholder="Enter strong password"
                  className="input-field"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="toggle-password"
                >
                  <span className="eye-icon">👁️</span>
                </button>
              </div>

              <div className="validation-row">
                <span
                  className="validation-circle"
                  style={{ backgroundColor: getCriteriaColor(specialCharMet) }}
                >
                  One Special Character
                </span>
                <span
                  className="validation-circle"
                  style={{ backgroundColor: getCriteriaColor(numberMet) }}
                >
                  One Number
                </span>
                <span
                  className="validation-circle"
                  style={{ backgroundColor: getCriteriaColor(upperCaseMet) }}
                >
                  One Uppercase character
                </span>
                <span
                  className="validation-circle"
                  style={{ backgroundColor: getCriteriaColor(minLengthMet) }}
                >
                  8 Character Min
                </span>

                <span
                  className="validation-circle"
                  style={{ backgroundColor: getCriteriaColor(lowerCaseMet) }}
                >
                  One Lowercase character
                </span>
              </div>
              {errors.password && <p className="error">{errors.password}</p>}
            </div>

            <button type="submit" className="create-account-btn">
              Create Account <span className="arrow">→</span>
            </button>
          </form>

          <hr className="separator" />
          <h3 className="continue-heading">Or Continue With</h3>
          <div className="social-login">
            <button
              type="button"
              className="social-btn"
              onClick={() => handleSocialAuth("google", selectedRole)}
            >
              <img
                src="../images/google.png"
                alt="Google"
                className="social-icon"
              />
              <span className="social-text">Google</span>
            </button>
            <button
              type="button"
              className="social-btn"
              onClick={() => handleSocialAuth("facebook", selectedRole)}
            >
              <img
                src="../images/facebook-logo.png"
                alt="Facebook"
                className="social-icon"
              />
              <span className="social-text">Facebook</span>
            </button>
            <button
              type="button"
              className="social-btn"
              onClick={() => handleSocialAuth("apple", selectedRole)}
            >
              <img
                src="../images/apple.png"
                alt="Apple"
                className="social-icon"
              />
              <span className="social-text">Apple</span>
            </button>
          </div>

          <p className="signin-text">
            Already have an account?{" "}
            <a href="/login" className="signin-link">
              Sign In
            </a>
          </p>
        </div>
      </div>

      <OTPModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        email={email}
        phone={phone}
        onVerify={verifyOtp}
        loading={otpLoading}
        error={otpError}
        onResend={handleResendOtp}
      />

      {/* Right side: Image with text + dots */}
      <div className="image-section">
        <div className="image-container">
          {images.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={`Slide ${index + 1}`}
              className={index === currentImageIndex ? "active-image" : "image"}
            />
          ))}
          <div className="image-text">
            <h2>{imageTexts[currentImageIndex].title}</h2>
            <p>{imageTexts[currentImageIndex].subtitle}</p>
          </div>
          <div className="dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`dot ${
                  index === currentImageIndex ? "active-dot" : ""
                }`}
                onClick={() => setCurrentImageIndex(index)}
              ></span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;
